import { Typography } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"

import Icon from "../../../../../Icon"
import Images from "../../../../../Images"
import { paymentProcessorTitle } from "../../../../../Utils/utils"
import {
  selectActiveProcessor,
  selectBuildingBlocks,
  selectConnectedProcessors,
  setActiveProcessor
} from "../redux/configure.slice"
import { commitSection } from "../redux/configure.thunks"
import { isExperimentalContentStep, stepSections } from "../schema/sections"
import { processorStatuses } from "../utils/sectionProgress"

import styles from "../configure.module.less"

const { Text } = Typography

// Readable processor name for the "in progress" badge (falls back to a
// capitalized slug for anything not in the shared title map).
const procTitle = (slug) =>
  paymentProcessorTitle?.[slug] || (slug ? slug.charAt(0).toUpperCase() + slug.slice(1) : slug)

// Both of these render only with several processors connected, and never on Integrate
// itself (step 1 is shared: it's where the merchant decides how many processors there
// will be, so a per-processor breakdown doesn't apply yet) — that step simply doesn't
// render them.
const useMultiProcessor = () => {
  const activeProcessor = useSelector(selectActiveProcessor)
  const connectedProcessors = useSelector(selectConnectedProcessors)
  return {
    activeProcessor,
    connectedProcessors,
    multiProcessor: (connectedProcessors?.length || 0) > 1,
    activeSlug: activeProcessor || connectedProcessors?.[0]
  }
}

// Prominent "you are here" banner on steps 2-6: states which connected processor
// the current step is being filled for, and its position in the set, so the
// per-processor split is obvious from the main pane (not just the rail/switcher).
export const ActiveProcessorBanner = () => {
  const { t } = useTranslation()
  const { connectedProcessors, multiProcessor, activeSlug } = useMultiProcessor()
  if (!multiProcessor) return null

  const activeIdx = connectedProcessors.indexOf(activeSlug)
  return (
    <div className={styles.activeProcBanner}>
      <span className={styles.activeProcBannerLogo}>
        {Images[`integration_${activeSlug}`] ? (
          <img
            src={Images[`integration_${activeSlug}`]}
            alt={procTitle(activeSlug)}
            width={28}
            height={28}
          />
        ) : (
          <Icon type="card" style={{ fontSize: 20 }} />
        )}
      </span>
      <span className={styles.activeProcBannerText}>
        <span className={styles.activeProcBannerTitle}>
          {t("Setting up _name_", { name: procTitle(activeSlug) })}
        </span>
        <span className={styles.activeProcBannerSub}>
          {t("Processor _n_ of _total_", {
            n: activeIdx >= 0 ? activeIdx + 1 : 1,
            total: connectedProcessors.length
          })}
        </span>
      </span>
    </div>
  )
}

// The ONE place the merchant picks which processor they're working on; it sits at the
// top of the main pane (not the rail, which is too dynamic a place for a control this
// important). Reads the RAW building blocks (with the byProcessor map) so it can show
// each connected processor's own completion status.
const ProcessorSwitcher = ({ sectionKey }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { activeProcessor, connectedProcessors, multiProcessor } = useMultiProcessor()
  const fullBuildingBlocks = useSelector(selectBuildingBlocks)
  if (!multiProcessor) return null

  return (
    <div className={styles.procSwitch}>
      <Text className={styles.procListLabel}>{t("Switch processor")}</Text>
      <div className={styles.procSwitchRow}>
        {processorStatuses(fullBuildingBlocks, connectedProcessors).map(
          ({ slug, done, total, complete }) => {
            const active = slug === (activeProcessor || connectedProcessors[0])
            const logo = Images[`integration_${slug}`]
            return (
              <button
                key={slug}
                type="button"
                aria-pressed={active}
                className={`${styles.procItem} ${active ? styles.active : ""}`}
                onClick={() => {
                  if (slug === activeProcessor) return
                  // Commit the section we're leaving so switching processors never
                  // drops in-progress edits. A content step spans several buckets and
                  // its own key is not one, so commit each bucket instead.
                  const sections = isExperimentalContentStep(sectionKey)
                    ? stepSections(sectionKey)
                    : [sectionKey]
                  sections.forEach((section) => dispatch(commitSection({ section })))
                  dispatch(setActiveProcessor(slug))
                }}
              >
                <span className={styles.procItemLogo}>
                  {logo ? (
                    <img src={logo} alt={procTitle(slug)} width={24} height={24} />
                  ) : (
                    <Icon type="card" style={{ fontSize: 16 }} />
                  )}
                </span>
                <span className={styles.procItemName}>{procTitle(slug)}</span>
                <span className={styles.procItemStatus}>
                  {complete ? (
                    <>
                      <Icon type="circle_check_green" style={{ fontSize: 16 }} /> {t("Done")}
                    </>
                  ) : (
                    t("_done_ of _total_", { done, total })
                  )}
                </span>
              </button>
            )
          }
        )}
      </div>
    </div>
  )
}

export default ProcessorSwitcher
